import { useEffect, useRef, useState } from 'react'
import {
    Card,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    InputGroup,
    Skeleton,
    Text,
    Image,
    useToast,
    Checkbox, Link
} from '@chakra-ui/react'
import { Form, Formik, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'
import ReCAPTCHA from 'react-google-recaptcha'
import * as Yup from 'yup'

import { SendMessageResponse, SendMessageData, Department } from '@core/app/types/contactsApiType'
import { useSendMessageMutation } from '@core/app/api/contactsApi'
import { AppButton } from '@core/components/AppButton'
import { RECAPTCHA } from '@core/utils/constants/constants'
import { FlagSelect } from '@core/components/FlagSelect/FlagSelect'
import { AutoResizeTextarea } from '@core/components/ResizeTextarea/ResizeTextarea'

import Notification from './Notification/Notification'

import success from '@core/assets/images/success.svg'
import warning from '@core/assets/images/warning.svg'
import RecaptchaIcon from '@core/assets/icons/recaptcha.svg'

interface ContactFormProps {
    isLoading: boolean;
    selectedDepartment: Department | null;
}

const ContactForm = ({ isLoading, selectedDepartment }: ContactFormProps) => {
    const { t } = useTranslation()
    const toast = useToast()
    const [sendData] = useSendMessageMutation()
    const [departmentId, setDepartmentId] = useState<number>(0)
    const [apiErrors, setApiErrors] = useState<SendMessageResponse & {agreement?: string}>({})
    const recaptchaRef = useRef<ReCAPTCHA>(null)
    const formRef = useRef<FormikProps<SendMessageData & {agreement: boolean}>>(null)
    const phoneRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
        if (selectedDepartment?.department?.departmentId) {
            setDepartmentId(selectedDepartment.department.departmentId)
        }
    }, [selectedDepartment])

    const initialValues: SendMessageData & {agreement: boolean}  = {
        name: '',
        email: '',
        phone: {
            prefix: '',
            number: ''
        },
        message: '',
        departmentId: departmentId,
        'g-recaptcha-response': '',
        agreement: false
    }

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(`${t('6455')}`),
        email: Yup.string()
            .required(`${t('6456')}`)
            .email(`${t('6598')}`),
        phone: Yup.object().shape({
            prefix: Yup.string().required(`${t('6609')}`),
            number: Yup.string()
                .required(`${t('6744')}`)
                .matches(/^[0-9].{5,12}$/, `${t('4724')}`),
        }),
        message: Yup.string().required(`${t('3924')}`),
        agreement: Yup.boolean().oneOf([true], `${t('3924')}`)
    })

    const formSubmithandler = async (values: SendMessageData & {agreement: boolean}, setSubmitting: (isSubmitting: boolean) => void) => {
        setApiErrors({})
        recaptchaRef.current?.reset()
        const token = await recaptchaRef.current?.executeAsync()
        const { agreement, ...res } = values

        if (token) {
            const data = { ...res, 'g-recaptcha-response': token }
            try {
                await sendData(data).unwrap()
                toast({
                    duration: 5000,
                    position: 'top',
                    render: () => <Notification bg='state.success' message={t('7812')} icon={success} />
                })
                formRef.current?.resetForm()
            } catch (error: any) {
                if (error?.data?.data?.errors) {
                    setApiErrors(error.data.data.errors)
                } else {
                    toast({
                        duration: 5000,
                        position: 'top',
                        render: () => <Notification bg='state.warning' message={t('7811')} icon={warning} />
                    })
                }
            } finally {
                setSubmitting(false)
            }
        }
    }

    return (
        <Card
            bg={'text.white'}
            w={{ base: '100%', xl: '384px' }}
            py={{ base: '24px', xl: '32px' }}
            px={{ base: '24px', xl: '32px' }}
            ml={'auto'}
        >
            <Flex flexDirection={'column'}>
                {isLoading ? (
                    <Skeleton w="140px" height="30px" mb={4}/>
                ) : (
                    <Text fontSize={'20px'} color={'text.main'} fontWeight={'600'} mb={4}>
                        {t('452')}
                    </Text>
                )}
                <Formik
                    innerRef={formRef}
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={(values, { setSubmitting }) => {
                        formSubmithandler(values, setSubmitting)
                    }}
                >
                    {({
                        values,
                        errors,
                        touched,
                        isSubmitting,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                    }) => (
                        <Form onSubmit={handleSubmit} style={{ marginBottom: '0px' }}>
                            {isLoading ? (
                                <Skeleton w="full" height="69px" mb={4}/>
                            ) : (
                                <FormControl
                                    isInvalid={!!(errors.name && touched.name) || !!apiErrors.name}
                                    mb={4}
                                >
                                    <FormLabel>
                                        <Text fontSize={'xs'}>{t('63')}*</Text>
                                    </FormLabel>
                                    <Input
                                        type={'text'}
                                        name={'name'}
                                        value={values.name}
                                        onChange={(e) => {
                                            handleChange(e)
                                            setApiErrors(prevState => ({ ...prevState, name: '' }))
                                        }}
                                        onBlur={handleBlur}
                                        placeholder={`${t('7766')}`}
                                    />
                                    <FormErrorMessage>
                                        {touched.name && errors.name ? errors.name : apiErrors.name}
                                    </FormErrorMessage>
                                </FormControl>
                            )}
                            {isLoading ? (
                                <Skeleton w="full" height="69px" mb={4}/>
                            ) : (
                                <FormControl
                                    isInvalid={!!(errors.email && touched.email) || !!apiErrors.email}
                                    mb={4}
                                >
                                    <FormLabel>
                                        <Text fontSize={'xs'}>{t('92')}*</Text>
                                    </FormLabel>
                                    <Input
                                        type={'text'}
                                        name={'email'}
                                        value={values.email}
                                        onChange={(e) => {
                                            handleChange(e)
                                            setApiErrors(prevState => ({ ...prevState, email: '' }))
                                        }}
                                        onBlur={handleBlur}
                                        placeholder={`${t('727')}`}
                                    />
                                    <FormErrorMessage>
                                        {touched.email && errors.email ? errors.email : apiErrors.email}
                                    </FormErrorMessage>
                                </FormControl>
                            )}
                            {isLoading ? (
                                <Skeleton w="full" height="69px" mb={4}/>
                            ) : (
                                <FormControl mb={4}>
                                    <FormLabel>{t('2')}*</FormLabel>
                                    <InputGroup gap={1} isolation={'auto'}>
                                        <FormControl
                                            maxW={28}
                                            isInvalid={!!(errors.phone?.prefix && touched.phone?.prefix) || !!apiErrors.phone?.prefix}
                                        >
                                            <FlagSelect
                                                type={'default'}
                                                onChange={(value) => {
                                                    setFieldValue('phone.prefix', value)
                                                    setApiErrors(prevState => ({
                                                        ...prevState,
                                                        phone: {
                                                            ...prevState.phone,
                                                            prefix: ''
                                                        }
                                                    }))
                                                }}
                                                exist={() => phoneRef.current?.focus()}
                                            />
                                            <FormErrorMessage>
                                                {errors.phone?.prefix ? errors.phone?.prefix : apiErrors.phone?.prefix}
                                            </FormErrorMessage>
                                        </FormControl>
                                        <FormControl
                                            isInvalid={!!(errors.phone?.number && touched.phone?.number) || !!apiErrors.phone?.number}
                                        >
                                            <Input
                                                ref={phoneRef}
                                                type={'text'}
                                                name={'phone.number'}
                                                value={values.phone.number}
                                                onChange={e => {
                                                    const value = e.target.value
                                                    if (value === '' || /^[0-9]+$/.test(value)) {
                                                        handleChange(e)
                                                    }
                                                    setApiErrors(prevState => ({
                                                        ...prevState,
                                                        phone: {
                                                            ...prevState.phone,
                                                            number: ''
                                                        }
                                                    }))
                                                }}
                                                onBlur={handleBlur}
                                                placeholder={`${t('6618')}`}
                                                maxLength={13}
                                            />
                                            <FormErrorMessage>
                                                {touched.phone?.number && errors.phone?.number ? errors.phone?.number : apiErrors.phone?.number}
                                            </FormErrorMessage>
                                        </FormControl>
                                    </InputGroup>
                                </FormControl>
                            )}
                            {isLoading ? (
                                <Skeleton w="full" height="142px" mb={4}/>
                            ) : (
                                <FormControl isInvalid={!!(errors.message && touched.message) || !!apiErrors.message}>
                                    <FormLabel>{t('32')}*</FormLabel>
                                    <AutoResizeTextarea
                                        bg={'bg.1'}
                                        placeholder={`${t('7769')}`}
                                        fontSize={'sm'}
                                        value={values.message}
                                        onChange={(e: any) => {
                                            setFieldValue('message', e.target.value)
                                            setApiErrors(prevState => ({ ...prevState, message: '' }))
                                        }}
                                        maxLength={1500}
                                        size={'xl'}
                                        name={'message'}
                                    />
                                    <FormErrorMessage>
                                        {touched.message && errors.message ? errors.message : apiErrors.message}
                                    </FormErrorMessage>
                                </FormControl>
                            )}
                            <FormControl isInvalid={!!(errors.agreement && touched.agreement)}>
                                <Checkbox mt={'16px'} size={'md'} isChecked={values.agreement} onChange={
                                    (e) => {
                                        setFieldValue('agreement', e.target.checked)
                                        setApiErrors(prevState => ({ ...prevState, agreement: '' }))
                                    }
                                }>
                                    <Text fontSize={'sm'} color={'text.main'}>
                                        {t('6620')}{' '}
                                        <Link href={'/user-agreement'} target={'_blank'} color={'text.link'}>
                                            {t('4635')}
                                        </Link>
                                        {', '}
                                        <Link href={'/privacy-policy'} target={'_blank'} color={'text.link'}>
                                            {t('4636')}
                                        </Link>{' '}
                                        <Text display={'inline'} textTransform={'lowercase'}>
                                            {t('2504')}
                                        </Text>{' '}
                                        <Link href={'/cookie-notice'} target={'_blank'} color={'text.link'}>
                                            {t('4637')}
                                        </Link>
                                        .
                                    </Text>
                                </Checkbox>
                                <FormErrorMessage>
                                    {touched.agreement && errors.agreement ? errors.agreement : apiErrors.agreement}
                                </FormErrorMessage>
                            </FormControl>
                            {isLoading ? (
                                <Skeleton w="176px" height="44px" borderRadius="30px" mt={6}/>
                            ) : (
                                <Flex justifyContent={'space-between'} mt={6} alignItems={'center'} gap={'12px'}>
                                    <FormControl>
                                        <AppButton
                                            translate="no"
                                            title={t('452')}
                                            type={'submit'}
                                            isLoading={isSubmitting}
                                            variant={'primary'}
                                            whiteSpace={'pre-line'}
                                            minW={'176px'}
                                            maxW={'265px'}
                                            minH={'44px'}
                                            height={'100%'}
                                            px={'24px'}
                                            py={'12px'}
                                        />
                                    </FormControl>
                                    <Image src={RecaptchaIcon} width={"44px"} height={"44px"}/>
                                </Flex>
                            )}
                        </Form>
                    )}
                </Formik>
                <ReCAPTCHA
                    sitekey={RECAPTCHA}
                    ref={recaptchaRef}
                    size={'invisible'}
                    onExpired={() => {
                        recaptchaRef.current?.reset()
                    }}
                />
            </Flex>
        </Card>
    )
}

export default ContactForm
